import type { Product } from "../../products";
import { BUNDLE_CHECKOUT_URL, PRICING } from "@/lib/pricing-constants";
import { getProductScreenshots } from "@/lib/utils/get-product-screenshots";

export const xUnfollowProProduct: Product = {
  id: "x-unfollow-pro",
  slug: "x-unfollow-pro",
  name: "Unfollow Pro for X (Twitter) — Paced Bulk Unfollow & Filters",
  alternateName: "Unfollow Pro for X (Twitter)",
  shortName: "Unfollow Pro for X",
  url: "https://plugmonkey.xyz/product/x-unfollow-pro/",
  chromeStoreRedirect: "/redirect/x-unfollow-pro-chrome-store/",
  description:
    "Bulk unfollow on X/Twitter with throttle controls: three pacing speeds, auto-pause on rate limits, VIP whitelist, mutual skip. Free Chrome extension.",
  shortDescription: "Search, Filter, and Unfollow X (Twitter) Accounts With Surgical Precision",
  image: ["https://plugmonkey.xyz/product/x-unfollow-pro/og-image.png"],
  icon: {
    name: "HiUserRemove",
    backgroundClasses: "bg-black",
    color: "#ffffff",
  },
  category: "Software > Browser Extensions > Social Media Management",
  applicationCategory: "BrowserApplication",
  operatingSystem: "Chrome",
  audienceType: "Social Media Users",
  freeOffer: {
    name: "Free Version",
    description: "Unfollow up to 10 accounts per week with advanced filtering features",
    price: "0",
    priceCurrency: "USD",
    url: "https://plugmonkey.xyz/redirect/x-unfollow-pro-chrome-store/",
  },
  offers: {
    monthly: {
      name: "Monthly Subscription",
      description: "Pay as you go. Cancel anytime.",
      price: PRICING.MONTHLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/1ea68a92-5a25-4d08-a7ae-8e066f768fa3",
    },
    yearly: {
      name: "Yearly Subscription",
      description: `Save ~${PRICING.YEARLY_SAVINGS_PERCENT}% vs monthly billing.`,
      price: PRICING.YEARLY,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/924df075-295b-4cc2-ac9f-77182e80d2cd",
    },
    lifetime: {
      name: "Lifetime License",
      description: "One-time payment. Own it forever.",
      price: PRICING.LIFETIME,
      priceCurrency: "USD",
      url: "https://products.plugmonkey.xyz/checkout/buy/f3048292-2de4-4ec5-ba4d-c4f0fbf6b9ae",
      priceValidUntil: "2030-12-31",
      warranty: {
        durationOfWarranty: "P30D",
        warrantyScope: "30-day money-back guarantee",
      },
    },
  },
  bundleOffer: {
    name: "All-Access Bundle",
    description: "Get every extension we build, forever.",
    price: PRICING.BUNDLE_LIFETIME,
    priceCurrency: "USD",
    url: BUNDLE_CHECKOUT_URL,
  },
  aggregateRating: {
    ratingValue: "4.8",
    reviewCount: "156",
    bestRating: "5",
    worstRating: "1",
  },
  userCount: "28,470",
  keywords:
    "X unfollow, Twitter unfollow, bulk unfollow, social media management, Chrome extension, account curation",
  additionalProperties: [
    {
      name: "Platform",
      value: "Chrome Extension",
    },
    {
      name: "Version",
      value: "1.7.12",
    },
    {
      name: "License Type",
      value: "Lifetime",
    },
  ],
  version: "1.7.12",
  hero: {
    headline:
      "<span class='text-primary'>Unfollow Pro for X</span> — Bulk Unfollow 𝕏 (Twitter) Accounts With Surgical Precision",
    subheadline:
      "Unfollow X (Twitter) accounts that no longer serve you. Perfect for precise account curation, bulk management, and optimizing your timeline.",
    ctaText: "Install Free",
    badgeText: "v1.7.12 • Last updated Nov 3, 2026",
  },
  features: [
    {
      title: "Smart Unfollowing",
      description:
        "Automatically identify and unfollow inactive accounts, spam bots, and users who don't follow you back.",
      icon: "HiUserRemove",
    },
    {
      title: "Safe & Secure",
      description:
        "Chrome extension that works directly in your browser. No passwords, no API keys, no third-party access.",
      icon: "HiShieldCheck",
    },
    {
      title: "Bulk Operations",
      description:
        "Queue every matching account in one click. The run paces itself between unfollows and pauses on its own when X pushes back.",
      icon: "HiLightningBolt",
    },
  ],
  longFormFeatures: [
    {
      title: "Bulk Unfollow",
      description:
        "Unfollow every account that matches your current search or filter in one paced run, without clicking through them one by one.",
      bullets: [
        "Unfollow all filtered results",
        "Three pacing speeds: Normal 5-10s (default), Conservative 15-25s, Fast 2-3s",
        "Automatic breaks: a short rest after every 25, 50, 100 (default) or 200 unfollows",
        "Pauses with a countdown when X returns a rate limit",
      ],
    },
    {
      title: "Smart Filters",
      description:
        "Filter accounts by activity, follower ratio, or last post date to make informed decisions.",
    },
    {
      title: "Inactive Account Detection",
      description:
        "Filter by date since last posted to identify and unfollow inactive accounts that haven't engaged in 30+ days.",
    },
    {
      title: "Whitelist Protection",
      description:
        "Create a whitelist of accounts you never want to unfollow, ensuring you don't lose important connections.",
    },
  ],
  faq: [
    {
      question: "How does Unfollow Pro for X (Twitter) work?",
      answer:
        "Unfollow Pro for X (Twitter) is a Chrome extension that adds an orange button directly on X.com. Click it to open a panel where you can search, filter, and unfollow accounts based on criteria like inactivity, follower count, or verification status. The free version allows you to unfollow up to 10 accounts per week, while the Pro version offers unlimited unfollows and bulk actions.",
    },
    {
      question: "Is Unfollow Pro for X (Twitter) safe to use?",
      answer:
        "It is built to keep the risk low. Every unfollow is sent from your own logged-in browser session, with a randomized gap before the next one: Normal (5-10 seconds, the default), Conservative (15-25) or Fast (2-3), plus a short automatic break after every 100 unfollows by default (25, 50, 100 or 200, or off), lasting about a quarter of the time that batch took. When X returns a rate limit, the run pauses with a countdown, slows down, and stops after a fourth refusal in a row. No tool can guarantee X will never restrict an account, so for a large clean-up choose Normal or Conservative and spread it over several sessions. We don't store your X credentials or personal data, and we never sell or share your information.",
    },
    {
      question: "What is the difference between the Free and Pro versions?",
      answer:
        "The Free version allows you to unfollow up to 10 accounts per week with advanced filtering. The Pro version offers unlimited unfollows, bulk actions (unfollow multiple at once), inactive account detection, and follower count filtering.",
    },
    {
      question: "Is the Pro license a subscription or a one-time payment?",
      answer:
        "You have options! We offer flexible Monthly and Yearly subscriptions, or you can choose the Lifetime License for a one-time payment to own it forever.",
    },
    {
      question: "Can I use Unfollow Pro for X (Twitter) on multiple devices?",
      answer:
        "Yes! Your Pro license is tied to your X (Twitter) account, so you can use Unfollow Pro for X (Twitter) on any device where you have the extension installed and are logged into the same X account. Simply install the extension on your other devices and activate the Pro features using the same X account that you purchased the license for.",
    },
    {
      question: "Will people know if I unfollow them using Unfollow Pro for X (Twitter)?",
      answer:
        "No, Unfollow Pro for X (Twitter) unfollows accounts in the same way as if you were doing it manually. The accounts you unfollow won't receive any special notification that you've used our tool. They may notice you've unfollowed them through X's standard notifications, just as they would with manual unfollows.",
    },
    {
      question: "How do I install Unfollow Pro for X (Twitter)?",
      answer:
        'Installation is simple. Click the "Install Free" button on this page, which will take you to the Chrome Web Store. Click "Add to Chrome" and the extension will be installed automatically. Once installed, visit X.com and you\'ll see an orange Unfollow Pro for X button on the page — click it to get started.',
    },
    {
      question: "What happens if X.com changes its interface?",
      answer:
        "We actively maintain Unfollow Pro for X (Twitter) and quickly adapt to any changes in X's interface. If X.com makes updates, we'll release a new version of our extension to ensure compatibility. All updates are free, even for Pro users.",
    },
    {
      question: "Can I get a refund if I'm not satisfied?",
      answer:
        "Yes, we offer a 30-day money-back guarantee. If Unfollow Pro for X (Twitter) isn't working as described, contact our support team within 30 days of purchase — we'll troubleshoot first (most issues resolve in a single reply), and if we can't resolve it, you get a full refund.",
    },
    {
      question:
        "How does Unfollow Pro for X (Twitter) compare to other tools like TweetDeleter or Circleboom?",
      answer:
        'Unlike TweetDeleter which focuses on deleting your tweets, Unfollow Pro for X (Twitter) specializes in managing who you follow. Compared to Circleboom, Unfollow Pro for X (Twitter) offers a more streamlined experience focused specifically on unfollowing with powerful filtering options. Unfollow Pro for X (Twitter) is also more affordable with its one-time payment model versus the subscription models of many competitors. Our tool integrates directly with your browser for a seamless experience without requiring you to authorize a third-party app with your X account credentials. Read our <a href="/comparison/x-unfollow-pro-vs-circleboom/" style="color: var(--primary); text-decoration: underline;">detailed head-to-head comparison with Circleboom</a> or see our <a href="/best/best-twitter-unfollow-tools/" style="color: var(--primary); text-decoration: underline;">full roundup of the best Twitter unfollow tools</a>.',
    },
  ],
  relatedResources: [
    {
      category: "best",
      slug: "best-twitter-unfollow-tools",
      title: "Best X (Twitter) Unfollow Tools 2026 — Ranked",
      description: "Independent ranking of the top X unfollow tools. Unfollow Pro for X ranks #1.",
    },
    {
      category: "guide",
      slug: "how-to-mass-unfollow-on-twitter",
      title: "How to Mass Unfollow on X / Twitter",
      description: "Step-by-step workflow with safety tips for accounts of any size.",
    },
    {
      category: "faq",
      slug: "how-does-whitelist-protect-work-x-unfollow-pro",
      title: "How Does Whitelist Protection Work?",
      description: "Why your VIP follows stay safe during bulk unfollow operations.",
    },
    {
      category: "faq",
      slug: "can-i-see-who-unfollowed-me-on-twitter",
      title: "Can I See Who Unfollowed Me on X / Twitter?",
      description: "The snapshot-comparison workflow + why most 'unfollow tracker' apps are scams.",
    },
    {
      category: "comparison",
      slug: "x-unfollow-pro-vs-circleboom",
      title: "Unfollow Pro for X vs Circleboom — Head-to-Head",
      description: "Pricing, features, safety, and verdict for the two top X unfollow tools.",
    },
    {
      category: "alternative",
      slug: "tweepi",
      title: "Tweepi Alternative — Why Unfollow Pro for X Wins",
      description: "Tweepi is defunct in 2026. Here's the working alternative.",
    },
  ],
  testimonials: [
    {
      text: "Went from following 4,200 accounts down to ~800 in a couple hours. The inactive filter caught accounts that hadn't posted in 2+ years. My timeline is a completely different experience now.",
      author: "Tomás R.",
      role: "Runs a 30K tech/startup account",
      rating: 5,
    },
    {
      text: "I manage 8 client accounts and the whitelist is the reason I upgraded. I can bulk clean without accidentally nuking their brand partnerships. Paid for itself on the first account.",
      author: "Priya N.",
      role: "Freelance social media consultant",
      rating: 5,
    },
    {
      text: "Tried three other unfollow tools before this. Two got my account rate-limited, one just stopped working. This one has built-in delays so it actually feels safe to use.",
      author: "Jake W.",
      role: "Design community account, 12K followers",
      rating: 4,
    },
    {
      text: "Did a follow-for-follow sprint two years ago and never cleaned up. Sorted by inactive and cut 1,900 dead accounts over three sessions. The built-in delays meant I never tripped a rate-limit warning.",
      author: "Devon L.",
      role: "Indie game dev, 9K followers",
      rating: 5,
    },
    {
      text: "My politics feed had become unusable. Bulk-unfollowed everyone I added during the 2022 midterms and kept my actual sources on the whitelist. Feels like a fresh account without starting over.",
      author: "Hana K.",
      role: "Newsletter writer",
      rating: 5,
    },
    {
      text: "Tried to do this by hand and lasted about 40 accounts before giving up. This ran through 600 non-followers while I made coffee. The delay makes a big cleanup slow, but I would rather that than a locked account.",
      author: "Bilal R.",
      role: "Musician promoting shows",
      rating: 4,
    },
  ],
  useCases: [
    {
      title: "Social Media Managers",
      description:
        "Maintain clean, professional following lists for client accounts. Quickly identify and remove inactive or low-quality accounts.",
    },
    {
      title: "Content Creators & Influencers",
      description:
        "Curate your following list to stay focused on accounts that align with your niche. Unfollow accounts that don't engage back.",
    },
    {
      title: "Marketing Professionals",
      description:
        "Research competitor followings and optimize brand accounts. Use follower count filtering to target specific segments.",
    },
    {
      title: "Active X Users",
      description:
        "Take back control of your feed. Remove accounts you followed years ago that no longer interest you.",
    },
  ],
  ctaSection: {
    title: "Ready to Clean Up Your 𝕏 Feed?",
    subtitle:
      "Join thousands of users who have transformed their 𝕏 experience with Unfollow Pro for X",
  },
  productHunt: {
    slug: "plugmonkey-s-xunfollow-pro",
    id: "x-unfollow-pro",
    title: "Unfollow Pro for X",
    rating: "4.8",
    reviewCount: "156",
  },
  demoSection: {
    title: "See Unfollow Pro for X in Action",
    description: "Watch a filtered, paced clean-up from start to finish.",
    slides: [
      ...getProductScreenshots("x-unfollow-pro"),
      {
        type: "video",
        src: "/product/x-unfollow-pro/demo.mp4",
        poster: "/product/x-unfollow-pro/optimized-images/screenshot-1-opt-1200.WEBP",
      },
    ],
  },
  howTo: {
    name: "How to Use Unfollow Pro for X (Twitter)",
    description:
      "Master advanced search, filtering, and bulk unfollow techniques with Unfollow Pro for X (Twitter) in just a few simple steps",
    totalTime: "PT5M",
    tool: "Chrome Browser",
    steps: [
      {
        name: "Install the Extension",
        text: "Add Unfollow Pro for X (Twitter) to Chrome with a single click. No registration required.",
        url: "https://plugmonkey.xyz/redirect/x-unfollow-pro-chrome-store/",
        position: 1,
      },
      {
        name: "Open 𝕏 and Click the On-Page Button",
        text: "Navigate to X.com and look for the orange Unfollow Pro for X button injected on the page. Click it to open the extension modal.",
        position: 2,
      },
      {
        name: "Select and Unfollow",
        text: "Apply advanced filters to select accounts for unfollow or protection, then execute bulk unfollow operations with precision.",
        position: 3,
      },
    ],
  },
  homepageCard: {
    backgroundIconName: "FaXTwitter",
    backgroundIconOpacity: "opacity-5",
    backgroundIconTransform: "transform rotate-12",
    features: [
      { label: "Pacing", value: "3 speeds + auto-pause" },
      { label: "Privacy", value: "Local Only" },
      { label: "Cost", value: "Free / Paid" },
    ],
  },
  displayOrder: 1,
  headerOrder: 1000, // Highest - shown first in header
  pricing: {
    tagline: "Search, Filter, and Unfollow 𝕏 (Twitter) Accounts With Surgical Precision",
    freeFeatures: [
      "10 unfollows per week",
      "Advanced filters (search by name, username, bio)",
      "Whitelist protection",
      "Basic analytics",
    ],
    proFeatures: [
      "Unlimited unfollows",
      "Bulk unfollow matching accounts",
      "CSV export of your filtered list",
      "Follower count filtering",
      "Inactive account detection",
      "Priority customer support",
      "All future updates included",
    ],
    useCase: "Perfect for curating your feed and removing clutter",
  },
  flowData: {
    installation: {
      headline: "You're ready to clean up your 𝕏 feed.",
      subheadline: "Here is how to get the most out of 𝕏 Unfollow Pro in 30 seconds.",
      guideSteps: [
        {
          title: "Pin the Extension",
          description:
            "Click the puzzle piece icon in your Chrome toolbar and pin 𝕏 Unfollow Pro for easy access.",
          image: "pin-extension",
        },
        {
          title: "Open Unfollow Pro for X on X.com",
          description:
            "Go to x.com, then look for the orange 'Open Unfollow Pro for X' button injected on the page. Click it to launch the filter panel.",
          image: "navigate-x",
          screenshot: "screenshot-1.png",
          ctaText: "Open X.com",
          ctaUrl: "https://x.com",
        },
        {
          title: "Filter & Unfollow",
          description:
            "Search by name or bio, filter by follower count or last post date, then unfollow individually or in bulk.",
          image: "start-unfollow",
          screenshot: "screenshot-2.png",
        },
      ],
      primaryCtaText: "Open X.com and Start Unfollowing",
      primaryCtaUrl: "https://x.com",
      emailCapture: {
        headline: "Get weekly growth tactics",
        description:
          "Join 10k+ power users receiving weekly tips on X growth and audience management. No fluff, just tactics.",
        buttonText: "Send me the tactics",
      },
    },
    uninstallation: {
      headline: "Sorry to see you go.",
      subheadline: "Feedback is a gift. Tell us how we can improve.",
      survey: {
        question: "What was the main reason for uninstalling?",
        options: [
          "It didn't work as expected",
          "I found a better alternative",
          "It's too expensive",
          "I don't need it anymore",
          "Other",
        ],
      },
    },
  },
};
